import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { useTranslation } from 'react-i18next';
import {
  GitBranch,
  Filter,
  Target,
} from 'lucide-react';

interface ConditionNodeData {
  label: string;
  type: string;
  icon?: string;
  config: Record<string, any>;
  isConfigured?: boolean;
}

const iconMap: Record<string, React.ReactNode> = {
  GitBranch: <GitBranch className="w-5 h-5" />,
  Filter: <Filter className="w-5 h-5" />,
  Target: <Target className="w-5 h-5" />,
};

const ConditionNode: React.FC<NodeProps<ConditionNodeData>> = ({ data, selected }) => {
  const { t } = useTranslation();

  const getConfigPreview = () => {
    if (data.config?.field && data.config?.operator) {
      const operators: Record<string, string> = {
        equals: '=',
        not_equals: '!=',
        contains: t('workflow.config.contains', 'contem'),
        greater_than: '>',
        less_than: '<',
      };
      return `${data.config.field} ${operators[data.config.operator] || data.config.operator} ${data.config.value || '?'}`;
    }
    return null;
  };

  const configPreview = getConfigPreview();

  return (
    <div
      className={`relative min-w-[200px] bg-white dark:bg-gray-800 rounded-xl shadow-lg border-2 transition-all ${
        selected
          ? 'border-orange-500 shadow-orange-100 dark:shadow-orange-900/30'
          : 'border-orange-200 dark:border-orange-800 hover:border-orange-400'
      }`}
    >
      {/* Handle - Input */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-4 !h-4 !bg-orange-500 !border-2 !border-white dark:!border-gray-800 !rounded-full"
      />

      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-t-lg px-4 py-2">
        <div className="flex items-center gap-2 text-white">
          <GitBranch className="w-4 h-4" />
          <span className="text-xs font-medium uppercase tracking-wide">{t('workflow.condition', 'Condicao')}</span>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center text-orange-600 dark:text-orange-400">
            {iconMap[data.icon || 'GitBranch'] || <GitBranch className="w-5 h-5" />}
          </div>
          <div className="flex-1">
            <p className="font-medium text-gray-900 dark:text-white text-sm leading-tight">
              {data.label}
            </p>
            {data.isConfigured ? (
              <span className="text-xs text-orange-600 dark:text-orange-400">{t('workflow.configured', 'Configurado')}</span>
            ) : (
              <span className="text-xs text-yellow-600 dark:text-yellow-400">{t('workflow.clickToConfigure', 'Clique para configurar')}</span>
            )}
          </div>
        </div>

        {/* Config Preview */}
        {configPreview && (
          <div className="mt-3 p-2 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">
              {configPreview}
            </p>
          </div>
        )}

        {/* Branch Labels */}
        <div className="mt-3 flex justify-between text-xs">
          <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
            <div className="w-2 h-2 rounded-full bg-green-500" />
            {t('workflow.yes', 'Sim')}
          </div>
          <div className="flex items-center gap-1 text-red-600 dark:text-red-400">
            {t('workflow.no', 'Nao')}
            <div className="w-2 h-2 rounded-full bg-red-500" />
          </div>
        </div>
      </div>

      {/* Handles - Two Outputs */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="yes"
        className="!w-4 !h-4 !bg-green-500 !border-2 !border-white dark:!border-gray-800 !rounded-full !left-[30%]"
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="no"
        className="!w-4 !h-4 !bg-red-500 !border-2 !border-white dark:!border-gray-800 !rounded-full !left-[70%]"
      />
    </div>
  );
};

export default memo(ConditionNode);
